
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  Download,
  Upload,
  FileText,
  RefreshCw,
  FileSpreadsheet,
  Plus,
  Eye,
  Edit,
  Trash2,
  Calendar,
  User,
  Building,
  Euro,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Activity
} from "lucide-react";
import AxiosInstance from "../axios/AxiosInstance";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import * as XLSX from 'xlsx';




// Fonctions utilitaires
const formatMontant = (montant) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(montant);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";

  // Gérer le format yyyyMMdd
  if (/^\d{8}$/.test(dateString)) {
    const year = parseInt(dateString.substring(0, 4), 10);
    const month = parseInt(dateString.substring(4, 6), 10) - 1; // mois = 0-indexé
    const day = parseInt(dateString.substring(6, 8), 10);
    const date = new Date(year, month, day);

    if (isNaN(date.getTime())) return "-";
    return format(date, "dd/MM/yyyy", { locale: fr });
  }

  // Sinon, essayer de parser normalement
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "-";

  return format(date, "dd/MM/yyyy", { locale: fr });
};


// Fonction pour obtenir le montant d'une facture
const getFactureMontant = (facture) => {
  return parseFloat(facture.TotalTTC || facture.TotalHT || 0);
};

// Fonction pour obtenir le montant restant
const getMontantRestant = (facture) => {
  const total = getFactureMontant(facture);
  const reglementPartiel = parseFloat(facture.TotalReglementPartiel || 0);
  return Math.max(0, total - reglementPartiel);
};

export default function FacturesPage() {
  // États du composant
  const navigate = useNavigate();
  const [facturesData, setFacturesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTab, setCurrentTab] = useState("all");
  const [sortField, setSortField] = useState("IdFacture");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // États pour la sélection multiple et validation
  const [selectedFactures, setSelectedFactures] = useState([]);
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
  const [validationComment, setValidationComment] = useState("");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Fonction pour charger les factures
  const loadFactures = () => {
    setIsLoading(true);
    AxiosInstance.get("facture/client")
      .then((data) => {
        setFacturesData(data.data);
        console.log(data.data);
        setIsLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.statusCode === 403) {
          localStorage.clear();
          navigate("/login");
        }
        setIsLoading(false);
      });
  };

  // Effet pour charger les données au montage
  useEffect(() => {
    loadFactures();
  }, []);

  // Fonction pour déterminer le statut d'une facture
  const getFactureStatut = (facture) => {
    if (facture.FactureRegle === "OUI" || facture.FactureRegle === "true") {
      return "soldée";
    }
    if (facture.TotalReglementPartiel && parseFloat(facture.TotalReglementPartiel) > 0) {
      return "partiellement payée";
    }
    return "non soldée";
  };

  // Fonction pour obtenir le montant d'une facture
  const getFactureMontant = (facture) => {
    return parseFloat(facture.TotalTTC || facture.TotalHT || 0);
  };

  // Fonction pour obtenir le montant restant
  const getMontantRestant = (facture) => {
    const total = getFactureMontant(facture);
    const reglementPartiel = parseFloat(facture.TotalReglementPartiel || 0);
    return total - reglementPartiel;
  };

  // Calcul des totaux pour l'affichage des statistiques
  const calculerTotaux = () => {
    if (!facturesData.length) {
      return {
        montantTotal: 0,
        montantDu: 0,
        montantFacturesSoldees: 0,
        montantFacturesNonSoldees: 0,
        montantFacturesPartielles: 0,
        facturesSoldees: 0,
        facturesNonSoldees: 0,
        facturesPartielles: 0,
        totalFactures: 0
      };
    }

    // Calcul pour toutes les factures
    const montantTotal = facturesData.reduce((acc, facture) => acc + getFactureMontant(facture), 0);

    // Calcul des montants par type de statut
    const montantFacturesSoldees = facturesData
      .filter(facture => getFactureStatut(facture) === "soldée")
      .reduce((acc, facture) => acc + getFactureMontant(facture), 0);

    const montantFacturesNonSoldees = facturesData
      .filter(facture => getFactureStatut(facture) === "non soldée")
      .reduce((acc, facture) => acc + getFactureMontant(facture), 0);

    const montantFacturesPartielles = facturesData
      .filter(facture => getFactureStatut(facture) === "partiellement payée")
      .reduce((acc, facture) => acc + getFactureMontant(facture), 0);

    // Calcul du montant restant à payer (montant dû)
    const montantDu = facturesData
      .filter(facture => getFactureStatut(facture) !== "soldée")
      .reduce((acc, facture) => {
        if (getFactureStatut(facture) === "partiellement payée") {
          return acc + getMontantRestant(facture);
        }
        return acc + getFactureMontant(facture);
      }, 0);

    // Comptage du nombre de factures par statut
    const facturesSoldees = facturesData.filter(facture => getFactureStatut(facture) === "soldée").length;
    const facturesNonSoldees = facturesData.filter(facture => getFactureStatut(facture) === "non soldée").length;
    const facturesPartielles = facturesData.filter(facture => getFactureStatut(facture) === "partiellement payée").length;

    return {
      montantTotal,
      montantDu,
      montantFacturesSoldees,
      montantFacturesNonSoldees,
      montantFacturesPartielles,
      facturesSoldees,
      facturesNonSoldees,
      facturesPartielles,
      totalFactures: facturesData.length
    };
  };

  const totaux = calculerTotaux();

  // Fonction de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Calcul des factures filtrées et triées
  const filteredFactures = facturesData
    .filter((facture) => {
      const statut = getFactureStatut(facture);

      // Filtre par onglet
      if (currentTab === "soldees" && statut !== "soldée") return false;
      if (
        currentTab === "nonSoldees" &&
        statut !== "non soldée" &&
        statut !== "partiellement payée"
      )
        return false;

      // Si champ vide, on garde tout
      if (searchTerm === "") return true;

      const search = searchTerm.toLowerCase();

      // Recherche dans tous les champs pertinents
      return (
        facture.RaisonSociale?.toLowerCase().includes(search) ||
        facture.IdFacture?.toString().includes(search) ||
        getFactureMontant(facture).toString().includes(search)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let valueA, valueB;

      // Mapping des champs pour la nouvelle entité
      switch (sortField) {
        case "statut":
          valueA = getFactureStatut(a);
          valueB = getFactureStatut(b);
          break;
        case "dateEcheance":
          valueA = a.DateEcheance;
          valueB = b.DateEcheance;
          break;
        case "datePaiement":
          valueA = a.DateReglement || a.dateDePaiement;
          valueB = b.DateReglement || b.dateDePaiement;
          break;
        case "montant":
          valueA = getFactureMontant(a);
          valueB = getFactureMontant(b);
          break;
        case "numero":
        case "IdFacture":
          // Tri numérique pour les numéros de facture
          valueA = parseInt(a.IdFacture) || 0;
          valueB = parseInt(b.IdFacture) || 0;
          break;
        case "client":
          valueA = a.RaisonSociale;
          valueB = b.RaisonSociale;
          break;
        default:
          valueA = a[sortField];
          valueB = b[sortField];
      }

      // Comparaison de dates
      if (sortField === "dateEcheance" || sortField === "datePaiement") {
        const dateA = valueA ? new Date(valueA) : null;
        const dateB = valueB ? new Date(valueB) : null;

        // Vérifier si les dates sont valides
        valueA = dateA && !isNaN(dateA.getTime()) ? dateA.getTime() : 0;
        valueB = dateB && !isNaN(dateB.getTime()) ? dateB.getTime() : 0;
      }

      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

  // S'assurer que currentPage est dans les limites valides
  const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages || 1));

  // Si la page actuelle n'est pas valide, la corriger
  if (validCurrentPage !== currentPage && totalPages > 0) {
    setCurrentPage(validCurrentPage);
  }

  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFactures = filteredFactures.slice(startIndex, endIndex);



  // Fonctions pour la sélection multiple
  const handleSelectAll = () => {
    if (selectedFactures.length === paginatedFactures.length) {
      setSelectedFactures([]);
    } else {
      setSelectedFactures(paginatedFactures.map(f => f._id));
    }
  };

  const handleSelectOne = (factureId) => {
    if (selectedFactures.includes(factureId)) {
      setSelectedFactures(selectedFactures.filter(id => id !== factureId));
    } else {
      setSelectedFactures([...selectedFactures, factureId]);
    }
  };

  // Fonction pour télécharger plusieurs factures
  const handleDownloadSelected = async () => {
    if (selectedFactures.length === 0) {
      toast.error("Veuillez sélectionner au moins une facture");
      return;
    }

    try {
      // Pour l'instant, on télécharge une par une
      for (const factureId of selectedFactures) {
        const facture = facturesData.find(f => f._id === factureId);
        if (facture) {
          await handleDownloadFacture(factureId, facture.IdFacture);
        }
      }
      toast.success(`${selectedFactures.length} facture(s) téléchargée(s)`);
      setSelectedFactures([]);
    } catch (error) {
      console.error("Erreur lors du téléchargement multiple:", error);
      toast.error("Erreur lors du téléchargement des factures");
    }
  };

  // Fonction pour valider plusieurs factures
  const handleValidateSelected = async () => {
    if (selectedFactures.length === 0) {
      toast.error("Veuillez sélectionner au moins une facture");
      return;
    }

    try {
      const response = await AxiosInstance.patch("facture/validate", {
        factureIds: selectedFactures,
        comment: validationComment
      });

      if (response.status === 200) {
        // Mettre à jour les factures dans l'état local
        const updatedFactures = facturesData.map(facture => {
          if (selectedFactures.includes(facture._id)) {
            return {
              ...facture,
              isValidated: true,
              validatedAt: new Date().toISOString(),
              validationComment: validationComment
            };
          }
          return facture;
        });

        setFacturesData(updatedFactures);
        toast.success(`${selectedFactures.length} facture(s) validée(s)`);
        setSelectedFactures([]);
        setIsValidationDialogOpen(false);
        setValidationComment("");
      }
    } catch (error) {
      console.error("Erreur lors de la validation:", error);
      toast.error("Erreur lors de la validation des factures");
    }
  };

  // Fonctions pour les actions
  const handleAddFacture = (newFacture) => {
    // TODO: Implémenter l'ajout de facture
    console.log("Ajouter facture:", newFacture);
  };

  const handlePartialPayment = (factureId, paiement) => {
    // TODO: Implémenter le paiement partiel
    console.log("Paiement partiel:", factureId, paiement);
  };

  const handleUpdateRelance = (factureId, niveau) => {
    // TODO: Implémenter la mise à jour de relance
    console.log("Mise à jour relance:", factureId, niveau);
  };

  // Fonction pour voir une facture
  const handleViewFacture = async (factureId) => {
    try {
      console.log("ookkokoko")
      const response = await AxiosInstance.get(`/facture/${factureId}/view`, {
        responseType: 'blob'
      });

      console.log("ookkokoko 2")


      // Créer une URL pour le blob PDF
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Ouvrir dans un nouvel onglet
      window.open(pdfUrl, '_blank');

      // Nettoyer l'URL après un délai
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
    } catch (error) {
      console.error("Erreur lors de l'affichage de la facture:", error);
      toast.error("Impossible d'afficher la facture");
    }
  };

  // Fonction pour télécharger une facture
  const handleDownloadFacture = async (factureId, numeroFacture) => {
    try {
      const response = await AxiosInstance.get(`/facture/${factureId}/download`, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture_${numeroFacture}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Facture téléchargée avec succès");
    } catch (error) {
      console.error("Erreur lors du téléchargement de la facture:", error);
      toast.error("Impossible de télécharger la facture");
    }
  };

  // Fonction utilitaire pour formater les dates de manière sécurisée
  const formatDateSafe = (dateValue) => {
    if (!dateValue) return '-';

    try {
      // Gérer le format yyyyMMdd (ex: 20251010)
      if (typeof dateValue === 'string' && /^\d{8}$/.test(dateValue)) {
        const year = parseInt(dateValue.substring(0, 4), 10);
        const month = parseInt(dateValue.substring(4, 6), 10) - 1; // mois = 0-indexé
        const day = parseInt(dateValue.substring(6, 8), 10);
        const date = new Date(year, month, day);

        if (isNaN(date.getTime())) return '-';
        return format(date, "dd/MM/yyyy", { locale: fr });
      }

      // Sinon, essayer de parser comme une date JavaScript normale
      const date = new Date(dateValue);
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) return '-';
      return format(date, "dd/MM/yyyy", { locale: fr });
    } catch (error) {
      console.warn("Date invalide:", dateValue, error);
      return '-';
    }
  };

  // Fonction pour exporter vers Excel
  const handleExportExcel = () => {
    try {
      // Préparer les données pour l'export avec gestion d'erreur pour chaque facture
      const dataToExport = filteredFactures.map((facture, index) => {
        try {
          return {
            'N° Facture': facture.IdFacture || '-',
            'Client': facture.RaisonSociale || '-',
            'Date Facture': formatDateSafe(facture.DateFacture),
            'Date Échéance': formatDateSafe(facture.DateEcheance),
            'Date Paiement': formatDateSafe(facture.DateReglement || facture.dateDePaiement),
            'Montant HT': facture.TotalHT || '-',
            'Montant TTC': facture.TotalTTC || '-',
            'Montant TVA': facture.TotalTVA || '-',
            'Paiement Partiel': facture.TotalReglementPartiel || '0',
            'Statut': getFactureStatut(facture) === "soldée" ? "Soldée" :
                     getFactureStatut(facture) === "partiellement payée" ? "Partiellement payée" : "Non soldée",
            'Type Facture': facture.TypeFacture || '-',
            'Règlement': facture.Reglement || '-',
            'SIRET': facture.SIRET || '-'
          };
        } catch (error) {
          console.error(`Erreur lors du traitement de la facture ${index}:`, facture, error);
          // Retourner une ligne avec des valeurs par défaut en cas d'erreur
          return {
            'N° Facture': facture.IdFacture || '-',
            'Client': facture.RaisonSociale || '-',
            'Date Facture': '-',
            'Date Échéance': '-',
            'Date Paiement': '-',
            'Montant HT': facture.TotalHT || '-',
            'Montant TTC': facture.TotalTTC || '-',
            'Montant TVA': facture.TotalTVA || '-',
            'Paiement Partiel': facture.TotalReglementPartiel || '0',
            'Statut': 'Erreur',
            'Type Facture': facture.TypeFacture || '-',
            'Règlement': facture.Reglement || '-',
            'SIRET': facture.SIRET || '-'
          };
        }
      });

      // Créer le workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataToExport);

      // Ajuster la largeur des colonnes
      const colWidths = [
        { wch: 12 }, // N° Facture
        { wch: 25 }, // Client
        { wch: 12 }, // Date Facture
        { wch: 12 }, // Date Échéance
        { wch: 12 }, // Date Paiement
        { wch: 12 }, // Montant HT
        { wch: 12 }, // Montant TTC
        { wch: 12 }, // Montant TVA
        { wch: 15 }, // Paiement Partiel
        { wch: 18 }, // Statut
        { wch: 15 }, // Type Facture
        { wch: 15 }, // Règlement
        { wch: 15 }  // SIRET
      ];
      ws['!cols'] = colWidths;

      // Ajouter la feuille au workbook
      XLSX.utils.book_append_sheet(wb, ws, "Factures");

      // Générer le nom du fichier avec la date
      const today = format(new Date(), "dd-MM-yyyy");
      const fileName = `factures_export_${today}.xlsx`;

      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);

      toast.success(`Export Excel réussi : ${dataToExport.length} factures exportées`);
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      toast.error("Erreur lors de l'export Excel");
    }
  };

  return (
    <div className="min-h-screen ">
      <motion.div
        className="container mx-auto py-8 px-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* En-tête moderne */}
        <motion.div
          className="mb-8"
          variants={itemVariants}
        >
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <FileText size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Gestion des Factures
              </h1>
              <p className="text-slate-600 mt-1">
                Suivez et gérez toutes vos factures en temps réel
              </p>
            </div>
          </div>
        </motion.div>

        {/* Statistiques modernes */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          variants={itemVariants}
        >
           {/* Total Factures */}
         {/*  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total Factures</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{totaux.totalFactures}</p>
                <p className="text-emerald-600 text-sm mt-1 flex items-center">
                  <Activity size={16} className="mr-1" />
                  Toutes périodes
                </p>
              </div>
              <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                <FileText size={24} className="text-white" />
              </div>
            </div>
          </div> */}

          {/* Montant Total */}
         {/*  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Montant Total</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{formatMontant(totaux.montantTotal)}</p>
                <p className="text-emerald-600 text-sm mt-1 flex items-center">
                  <TrendingUp size={16} className="mr-1" />
                  Chiffre d'affaires
                </p>
              </div>
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl">
                <Euro size={24} className="text-white" />
              </div>
            </div>
          </div> */}

          {/* Factures Soldées */}
         {/*  <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Factures Soldées</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{totaux.facturesSoldees}</p>
                <p className="text-emerald-600 text-sm mt-1 flex items-center">
                  <CheckCircle size={16} className="mr-1" />
                  {formatMontant(totaux.montantFacturesSoldees)}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-r from-emerald-500 to-green-600 rounded-xl">
                <CheckCircle size={24} className="text-white" />
              </div>
            </div>
          </div>

          {/* Montant Dû */}
          <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Reste à Payer</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{formatMontant(totaux.montantDu)}</p>
                <p className="text-red-600 text-sm mt-1 flex items-center">
                  <AlertCircle size={16} className="mr-1" />
                  {totaux.facturesNonSoldees + totaux.facturesPartielles} factures
                </p>
              </div>
              <div className="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-xl">
                <AlertCircle size={24} className="text-white" />
              </div>
            </div>
          </div>
        </motion.div>


        {/* Filtres et recherche modernes */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 mb-8"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            {/* Onglets de filtrage */}
            <div className="flex space-x-1 bg-slate-100 p-1 rounded-xl">
              <button
                onClick={() => {
                  setCurrentTab("all");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "all"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText size={18} />
                  <span>Toutes ({totaux.totalFactures})</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setCurrentTab("soldees");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "soldees"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <CheckCircle size={18} />
                  <span>Soldées ({totaux.facturesSoldees})</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setCurrentTab("nonSoldees");
                  setCurrentPage(1);
                }}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  currentTab === "nonSoldees"
                    ? "bg-white text-slate-800 shadow-md"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <AlertCircle size={18} />
                  <span>À payer ({totaux.facturesNonSoldees + totaux.facturesPartielles})</span>
                </div>
              </button>
            </div>

           
          </div>


           {/* Barre de recherche */}
            <div className="flex items-center mt-4 space-x-4">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher par client, numéro ou montant..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-10 pr-4 py-3 w-80 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setSearchTerm("")}
                    title="Effacer"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>

              <button
                onClick={() => setIsLoading(!isLoading)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
              >
                <RefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
                <span>Actualiser</span>
              </button>

              <button
                onClick={handleExportExcel}
                className="flex items-center space-x-2 px-4 py-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors"
                title="Exporter vers Excel"
              >
                <FileSpreadsheet size={18} />
                <span>Export Excel</span>
              </button>
            </div>

          {/* Actions en lot */}
          {selectedFactures.length > 0 && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <CheckCircle size={20} className="text-blue-600" />
                </div>
                <span className="text-blue-800 font-medium">
                  {selectedFactures.length} facture(s) sélectionnée(s)
                </span>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={handleDownloadSelected}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Download size={16} />
                  <span>Télécharger</span>
                </button>

                <button
                  onClick={() => setIsValidationDialogOpen(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <CheckCircle size={16} />
                  <span>Valider</span>
                </button>

                <button
                  onClick={() => setSelectedFactures([])}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <XCircle size={16} />
                  <span>Annuler</span>
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Sélection globale */}
        {paginatedFactures.length > 0 && (
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 mb-3"
            variants={itemVariants}
          >
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedFactures.length === paginatedFactures.length && paginatedFactures.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="text-slate-700 text-sm font-medium">
                Sélectionner toutes les factures de cette page ({paginatedFactures.length})
              </span>
            </div>
          </motion.div>
        )}

        {/* Liste des factures en cards modernes */}
        <motion.div
          className="space-y-4"
          variants={itemVariants}
        >
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredFactures.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm p-12 rounded-2xl shadow-lg border border-white/20 text-center">
              <FileText size={48} className="mx-auto text-slate-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-600 mb-2">Aucune facture trouvée</h3>
              <p className="text-slate-500">
                {searchTerm ? "Aucune facture ne correspond à votre recherche." : "Aucune facture disponible pour le moment."}
              </p>
            </div>
          ) : (
            <AnimatePresence>
              {paginatedFactures.map((facture, index) => (
                <motion.div
                  key={facture._id || facture.IdFacture}
                  className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  layout
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-center space-x-4">
                    {/* Checkbox de sélection */}
                    <input
                      type="checkbox"
                      checked={selectedFactures.includes(facture._id)}
                      onChange={() => handleSelectOne(facture._id)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
                    />

                    {/* Contenu principal */}
                    <div className="grid grid-cols-1 xl:grid-cols-6 lg:grid-cols-3 md:grid-cols-2 gap-4 items-center flex-1">
                     {/*  <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                        <Building size={20} className="text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 truncate">{facture.RaisonSociale || "-"}</p>
                        <p className="text-sm text-slate-500">Client</p>
                      </div>
                    </div> */}

                    {/* Numéro de facture */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                        <FileText size={20} className="text-purple-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 truncate">{facture.IdFacture || "-"}</p>
                        <p className="text-sm text-slate-500">N° Facture</p>
                      </div>
                    </div>

                    {/* Date de facture */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                        <Calendar size={20} className="text-indigo-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">{formatDate(facture.DateFacture)}</p>
                        <p className="text-sm text-slate-500">Date Facture</p>
                      </div>
                    </div>

                    {/* Date d'échéance */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0">
                        <Calendar size={20} className="text-amber-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">{formatDate(facture.DateEcheance)}</p>
                        <p className="text-sm text-slate-500">Échéance</p>
                      </div>
                    </div>

                    {/* Montant */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                        <Euro size={20} className="text-emerald-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800">{formatMontant(getFactureMontant(facture))}</p>
                        <p className="text-sm text-slate-500">Montant TTC</p>
                      </div>
                    </div>

                    {/* Statut */}
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className={`p-2 rounded-lg flex-shrink-0 ${
                        getFactureStatut(facture) === "soldée"
                          ? "bg-emerald-100"
                          : getFactureStatut(facture) === "partiellement payée"
                          ? "bg-blue-100"
                          : "bg-red-100"
                      }`}>
                        {getFactureStatut(facture) === "soldée" ? (
                          <CheckCircle size={20} className="text-emerald-600" />
                        ) : getFactureStatut(facture) === "partiellement payée" ? (
                          <Clock size={20} className="text-blue-600" />
                        ) : (
                          <AlertCircle size={20} className="text-red-600" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          getFactureStatut(facture) === "soldée"
                            ? "bg-emerald-100 text-emerald-800"
                            : getFactureStatut(facture) === "partiellement payée"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {getFactureStatut(facture) === "soldée"
                            ? "Soldée"
                            : getFactureStatut(facture) === "partiellement payée"
                            ? "Partielle"
                            : "Non soldée"
                          }
                        </div>
                        {getFactureStatut(facture) === "partiellement payée" && (
                          <p className="text-xs text-slate-500 mt-1">
                            Reste: {formatMontant(getMontantRestant(facture))}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 flex-shrink-0">
                      <button
                        onClick={() => handleViewFacture(facture._id)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Voir la facture"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleDownloadFacture(facture._id, facture.IdFacture)}
                        className="p-2 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                        title="Télécharger"
                      >
                        <Download size={18} />
                      </button>
                      {getFactureStatut(facture) !== "soldée" && (
                        <button
                          onClick={() => handlePartialPayment(facture._id)}
                          className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                          title="Paiement par carte"
                        >
                          <CreditCard size={18} />
                        </button>
                      )}
                    </div>
                    </div>
                  </div>

                  {/* Informations de validation */}
                  {facture.isValidated && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm">
                        <CheckCircle size={14} className="text-green-600 flex-shrink-0" />
                        <span className="text-green-800 font-medium">Validée</span>
                        {facture.validatedAt && (
                          <span className="text-green-700">
                            le {format(new Date(facture.validatedAt), "dd/MM/yyyy 'à' HH:mm", { locale: fr })}
                          </span>
                        )}
                        {facture.validatedBy && (
                          <span className="text-green-700 capitalize">
                            par {facture.validatedBy.firstname} {facture.validatedBy.lastname}
                          </span>
                        )}
                        {facture.validationComment && (
                          <span className="text-green-700 italic">
                            - {facture.validationComment}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </motion.div>

        {/* Pagination moderne */}
        {totalPages > 1 && (
          <motion.div
            className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/20 mt-8"
            variants={itemVariants}
          >
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-slate-600">
                Affichage de {startIndex + 1} à {Math.min(endIndex, filteredFactures.length)} sur {filteredFactures.length} factures
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, validCurrentPage - 1))}
                  disabled={validCurrentPage === 1}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                {totalPages > 0 && Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, validCurrentPage - 2)) + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        validCurrentPage === pageNum
                          ? "bg-blue-500 text-white"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, validCurrentPage + 1))}
                  disabled={validCurrentPage === totalPages || totalPages === 0}
                  className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Modal de validation */}
        <AnimatePresence>
          {isValidationDialogOpen && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle size={24} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Valider les factures
                  </h3>
                </div>

                <p className="text-slate-600 mb-4">
                  Vous êtes sur le point de valider {selectedFactures.length} facture(s).
                  Cette action confirmera que les factures ont été vérifiées.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Commentaire (optionnel)
                  </label>
                  <textarea
                    value={validationComment}
                    onChange={(e) => setValidationComment(e.target.value)}
                    placeholder="Ajouter un commentaire sur cette validation..."
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => {
                      setIsValidationDialogOpen(false);
                      setValidationComment("");
                    }}
                    className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleValidateSelected}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Valider les factures
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
